import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  BarChart3, 
  Key, 
  Home, 
  Building2, 
  Shield, 
  Users, 
  Award, 
  Clock,
  Phone,
  MessageCircle,
  Mail,
  ChevronRight
} from 'lucide-react';
import HeroSection from '../components/HeroSection';
import ServiceCard from '../components/ServiceCard';
import PropertyCard from '../components/PropertyCard';
import ValuationForm from '../components/ValuationForm';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const API = process.env.REACT_APP_BACKEND_URL;
const SERDAR_PHOTO = "/serdar-dogan-foto.jpg";
const HERO_BG = "https://images.unsplash.com/photo-1707082615632-b05c2097cca0?auto=format&fit=crop&w=1920&q=80";

const services = [
  {
    icon: BarChart3,
    title: 'Wertermittlung',
    description: 'Professionelle Immobilienbewertung nach aktuellen Marktdaten und fundierten Analysemethoden.',
    href: '/leistungen/wertermittlung'
  },
  {
    icon: Key,
    title: 'Immobilienverkauf',
    description: 'Diskrete Vermarktung & professionelle Verkaufsabwicklung für Ihre Immobilie.',
    href: '/leistungen/verkauf'
  },
  {
    icon: Home,
    title: 'Vermietung',
    description: 'Kompetente Begleitung von der Mietersuche bis zum Vertragsabschluss.',
    href: '/leistungen/vermietung'
  },
  {
    icon: Building2,
    title: 'Vermittlung',
    description: 'Vermittlung von Wohnungen, Liegenschaften & Gewerbeimmobilien.',
    href: '/leistungen/vermittlung'
  }
];

const trustSignals = [
  { icon: Award, text: 'Rhein-Main Spezialist' },
  { icon: Shield, text: 'Diskrete Abwicklung' },
  { icon: Users, text: 'Persönliche 1:1 Beratung' },
  { icon: Clock, text: 'Zertifizierte Wertermittlung' }
];

const faqItems = [
  {
    question: 'Wie läuft eine Immobilienbewertung ab?',
    answer: 'Nach Ihrer Kontaktaufnahme vereinbaren wir einen Besichtigungstermin. Ich analysiere Ihre Immobilie, den lokalen Markt und erstelle eine fundierte Werteinschätzung. Das Ergebnis besprechen wir persönlich.'
  },
  {
    question: 'Was kostet eine Wertermittlung?',
    answer: 'Die Ersteinschätzung ist für Sie kostenlos und unverbindlich. Für ein ausführliches Gutachten erstelle ich Ihnen gerne ein individuelles Angebot.'
  },
  {
    question: 'Welche Regionen betreuen Sie?',
    answer: 'Ich bin spezialisiert auf das Rhein-Main-Gebiet: Frankfurt, Wiesbaden, Darmstadt, Offenbach, Mainz, Bad Homburg, Hanau und Umgebung.'
  }
];

export default function HomePage() {
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/api/properties`);
      setProperties(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <HeroSection
        title="Ihr Immobilien­experte im Rhein-Main-Gebiet"
        highlightedWord="Rhein-Main-Gebiet"
        subtitle="Professionelle Wertermittlung. Diskreter Verkauf. Persönliche Beratung."
        primaryCTA={{ label: 'Immobilie bewerten lassen', href: '/leistungen/wertermittlung' }}
        secondaryCTA={{ label: 'Aktuelle Angebote', href: '/immobilien' }}
        backgroundImage={HERO_BG}
      />

      {/* Trust Signals */}
      <section className="bg-[#1a2640] border-y border-[#d1af6a]/20 py-8" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {trustSignals.map((signal, index) => (
              <motion.div
                key={signal.text}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex items-center gap-3"
              >
                <signal.icon className="w-5 h-5 text-[#d1af6a] flex-shrink-0" />
                <span className="text-white text-sm font-medium">{signal.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 sm:py-32" data-testid="intro-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={SERDAR_PHOTO}
                  alt="Serdar Dogan - Immobilienexperte"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-[#d1af6a] -z-10 hidden md:block" />
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="border-l-2 border-[#d1af6a] pl-6">
                <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">
                  ÜBER MICH
                </span>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-4 mb-6">
                  Kompetenz trifft Vertrauen
                </h2>
                <p className="text-[#c8c6c4] leading-relaxed mb-6">
                  Als studierter Betriebswirt und Immobilienexperte im Rhein-Main-Gebiet verbinde ich fundiertes Marktwissen mit persönlicher Beratung.
                  Jede Immobilie ist einzigartig – und verdient eine 
                  individuelle Betreuung, die über das Übliche hinausgeht.
                </p>
                <p className="text-[#c8c6c4] leading-relaxed mb-8">
                  Ob Verkauf, Vermietung oder Wertermittlung: Ich begleite Sie mit Diskretion, 
                  Engagement und dem Ziel, für Sie das bestmögliche Ergebnis zu erzielen.
                </p>
                <Link
                  to="/ueber-uns"
                  className="inline-flex items-center gap-2 text-[#d1af6a] font-medium hover:gap-4 transition-all"
                  data-testid="intro-link"
                >
                  Mehr über mich erfahren
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Valuation CTA Section */}
      <section className="py-24 sm:py-32 bg-[#0a1120]" data-testid="valuation-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border border-[#d1af6a]/30 p-8 md:p-12">
            <div className="text-center mb-10">
              <div className="gold-divider mb-6">
                <div className="diamond" />
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
                Was ist Ihre Immobilie wert?
              </h2>
              <p className="text-[#c8c6c4] max-w-2xl mx-auto">
                Erhalten Sie eine fundierte, kostenfreie Ersteinschätzung Ihres Objektes – 
                diskret, präzise und unverbindlich.
              </p>
            </div>

            {/* Steps */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              {[
                { num: '01', text: 'Formular ausfüllen' },
                { num: '02', text: 'Rückruf erhalten' },
                { num: '03', text: 'Kostenlose Bewertung' }
              ].map((step, i) => (
                <div key={step.num} className="flex items-center gap-4">
                  <span className="text-[#d1af6a] font-display text-2xl">{step.num}</span>
                  <span className="text-white text-sm">{step.text}</span>
                  {i < 2 && <div className="hidden md:block flex-1 h-px bg-[#d1af6a]/30" />}
                </div>
              ))}
            </div>

            <ValuationForm variant="compact" />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 sm:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">
              LEISTUNGEN
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-4">
              Unsere Leistungen
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <ServiceCard key={service.title} {...service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-24 bg-[#1a2640]" data-testid="quote-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="gold-divider mb-8">
            <div className="diamond" />
          </div>
          <blockquote className="font-display text-2xl sm:text-3xl text-white italic leading-relaxed mb-6">
            "Immobilien sind mehr als Quadratmeter – sie sind Lebensräume. 
            Ich begleite Sie bei jedem Schritt."
          </blockquote>
          <cite className="text-[#d1af6a] font-accent text-sm tracking-widest not-italic">
            – SERDAR DOGAN
          </cite>
        </div>
      </section>

      {/* Properties Section */}
      {properties.length > 0 && (
        <section className="py-24 sm:py-32" data-testid="properties-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
              <div>
                <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">
                  IMMOBILIEN
                </span>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-4">
                  Aktuelle Angebote
                </h2>
              </div>
              <Link
                to="/immobilien"
                className="inline-flex items-center gap-2 text-[#d1af6a] font-medium hover:gap-4 transition-all"
                data-testid="properties-link"
              >
                Alle Angebote anzeigen
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(Array.isArray(properties) ? properties : []).map((property, index) => (
                <PropertyCard key={property.id} property={property} index={index} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-24 sm:py-32 bg-[#0a1120]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-accent text-[#d1af6a] text-xs tracking-[0.2em]">
              FAQ
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-white mt-4">
              Häufige Fragen
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border border-[#d1af6a]/20 bg-[#1a2640] px-6"
              >
                <AccordionTrigger className="text-white hover:text-[#d1af6a] text-left py-5">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-[#c8c6c4] pb-5">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-8">
            <Link
              to="/faq"
              className="inline-flex items-center gap-2 text-[#d1af6a] font-medium hover:gap-4 transition-all"
              data-testid="faq-link"
            >
              Alle FAQ ansehen
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#1a2640] to-[#131d30] border-y border-[#d1af6a]/20" data-testid="contact-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl text-white mb-4">
            Bereit für den nächsten Schritt?
          </h2>
          <p className="text-[#c8c6c4] mb-8">
            Sprechen Sie direkt mit Serdar Dogan.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+491756900374"
              className="btn-primary flex items-center gap-2"
              data-testid="cta-phone"
            >
              <Phone className="w-4 h-4" />
              Anrufen
            </a>
            <a
              href="https://wa.me/491756900374?text=Guten%20Tag%20Herr%20Dogan%2C%20ich%20interessiere%20mich%20f%C3%BCr%20eine%20Immobilienberatung."
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex items-center gap-2"
              data-testid="cta-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </a>
            <a
              href="mailto:info@immobilien-dogan.com"
              className="btn-secondary flex items-center gap-2"
              data-testid="cta-email"
            >
              <Mail className="w-4 h-4" />
              E-Mail
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
